import { useEffect } from 'react';
import { useOutbox } from '../../store/outbox';
import { watchNetwork } from '../../lib/netStatus';

/** How often to try the queue while anything is waiting. */
const RETRY_MS = 60_000;

/**
 * Keeps the offline queue draining without anyone pressing anything.
 *
 * The Sync button exists so a cashier can ask "is my money in yet?" and get an
 * answer on demand — not because they should have to remember. So: on mount,
 * when the browser says the connection is back, when the tab regains focus,
 * and once a minute while the queue is not empty.
 *
 * All four converge on the same `sync()`, which is single-flight and safe to
 * call at any time: the server's durable idempotency check means a sale sent
 * twice still books once.
 */
export function useOfflineSync(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;

    const outbox = useOutbox.getState();
    void outbox.hydrate();

    const teardownNetwork = watchNetwork();

    const attempt = () => {
      const s = useOutbox.getState();
      if (!s.loaded || s.syncing) return;
      if (s.pendingCount() === 0) return;
      void s.sync();
    };

    const onOnline = () => attempt();
    const onFocus = () => attempt();
    window.addEventListener('online', onOnline);
    window.addEventListener('focus', onFocus);
    const timer = window.setInterval(attempt, RETRY_MS);

    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('focus', onFocus);
      window.clearInterval(timer);
      teardownNetwork();
    };
  }, [enabled]);
}
